import { RepairRequest, RepairStatus } from '../types/firestore';
import type {
  FulfillmentMode,
  FulfillmentSource,
  RepairRequestFulfillmentAddressSnapshot,
} from './fulfillment';

export interface RepairRequestFulfillmentCreateSelection {
  selectedMode: FulfillmentMode;
  selectedModeSource?: FulfillmentSource;
  address?: RepairRequestFulfillmentAddressSnapshot;
  note?: string;
}

export const REPAIR_REQUEST_PRIORITY_NOTE_MAX_LENGTH = 200;

export interface RepairRequestPriorityRequestCreateInput {
  requested: boolean;
  acceptsPotentialExtraFee: boolean;
  note?: string;
}

export interface RepairRequestCreateInput {
  clientId: string;
  clientRequestId?: string;
  serviceProviderId?: string;
  serviceId?: string;
  manualDeviceType?: string;
  brand?: string;
  model?: string;
  description?: string;
  urgency?: string;
  scheduledDate?: any;
  photos?: string[];
  clientNotes?: string;
  location?: RepairRequest['location'];
  clientDetails?: RepairRequest['clientDetails'];
  fulfillmentSelection?: RepairRequestFulfillmentCreateSelection;
  priorityRequest?: RepairRequestPriorityRequestCreateInput;
}

export const REPAIR_REQUEST_REVIEW_METADATA_KEYS = [
  'reviewSubmitted',
  'reviewId',
  'reviewSubmittedAt',
  'reviewInvitationSentAt',
] as const;

export const normalizeServiceProviderIdForCreate = (
  requestData: RepairRequestCreateInput
): string => {
  const normalizedServiceProviderId = String(requestData.serviceProviderId || '').trim();
  const normalizedServiceId = String(requestData.serviceId || '').trim();
  return String(normalizedServiceProviderId || normalizedServiceId || '').trim();
};

export const hasConflictingLegacyServiceId = (requestData: RepairRequestCreateInput): boolean => {
  const normalizedServiceProviderId = String(requestData.serviceProviderId || '').trim();
  const normalizedServiceId = String(requestData.serviceId || '').trim();

  return Boolean(
    normalizedServiceProviderId &&
      normalizedServiceId &&
      normalizedServiceProviderId !== normalizedServiceId
  );
};

const normalizeCreateTextField = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
};

export const buildRepairRequestCreatePayload = <TSentinel>(
  requestData: RepairRequestCreateInput,
  resolvedServiceProviderId: string,
  timestampFactory: () => TSentinel
) => {
  const manualDeviceType = normalizeCreateTextField(requestData.manualDeviceType);
  const priorityNote = normalizeCreateTextField(requestData.priorityRequest?.note);
  const priorityRequest = requestData.priorityRequest?.requested
    ? {
        requested: true,
        acceptsPotentialExtraFee: requestData.priorityRequest.acceptsPotentialExtraFee === true,
        ...(priorityNote ? { note: priorityNote } : {}),
      }
    : undefined;
  const createInput = {
    clientId: requestData.clientId,
    ...(requestData.clientRequestId ? { clientRequestId: requestData.clientRequestId } : {}),
    ...(manualDeviceType ? { manualDeviceType } : {}),
    ...(requestData.brand ? { brand: requestData.brand } : {}),
    ...(requestData.model ? { model: requestData.model } : {}),
    ...(requestData.description ? { description: requestData.description } : {}),
    ...(requestData.urgency ? { urgency: requestData.urgency } : {}),
    ...(requestData.scheduledDate ? { scheduledDate: requestData.scheduledDate } : {}),
    ...(Array.isArray(requestData.photos) ? { photos: requestData.photos } : {}),
    ...(requestData.clientNotes ? { clientNotes: requestData.clientNotes } : {}),
    ...(requestData.location ? { location: requestData.location } : {}),
    ...(requestData.clientDetails ? { clientDetails: requestData.clientDetails } : {}),
    ...(requestData.fulfillmentSelection
      ? { fulfillmentSelection: requestData.fulfillmentSelection }
      : {}),
    ...(priorityRequest ? { priorityRequest } : {}),
  };

  return {
    ...createInput,
    serviceProviderId: resolvedServiceProviderId,
    status: RepairStatus.NEW,
    createdAt: timestampFactory(),
    updatedAt: timestampFactory(),
  };
};
